'use client'

import CopyToClipboardIcon from '@assets/CopyToClipboardIcon'
import Button from '@components/Furry/Button'
import { Holding } from '@hooks/useHoldings'
import useRipple from '@hooks/useRipple'
import { cn } from '@utils/index'
import Image from 'next/image'
import { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Tooltip from './Tooltip'
import Toast from '@components/Toast'
import useStaking from '@hooks/useStaking'
import LoadingCircle from '@icons/loading-circle'
import UnstakeDialog from './UnstakeDialog'
import { useWallet } from '@solana/wallet-adapter-react'

type HoldingCardProps = {
  holding: Holding
  onSelectHolding?: (holding: Holding) => void
  ripple?: boolean
}

function HoldingCard({ holding, onSelectHolding, ripple }: HoldingCardProps) {
  const splitName = holding.name.split(' ')

  const { isStaked } = useStaking({
    nft: holding.publicKey,
  })

  const loadImageHandler = () => {
    const image = document.getElementById('img-' + holding.name)
    image.classList.remove('opacity-0')
    image.classList.add('animate-[fade-in_1s_ease-out]')
  }

  // TODO: Fix this
  const rippleRef = ripple
    ? // eslint-disable-next-line react-hooks/rules-of-hooks
      useRipple<HTMLSpanElement>({ duration: 500 })
    : undefined

  return (
    <span
      key={holding.name}
      className="flex flex-col border border-solid rounded-sm w-fit h-fit border-[rgb(55,57,62)]"
    >
      <span
        className="px-2 pt-2 w-full flex justify-center relative overflow-hidden opacity-0"
        id={'img-' + holding.name}
        ref={rippleRef}
      >
        <Tooltip multiplier={holding.aura ? 2 : 1} />
        <div>
          <Image
            onLoad={loadImageHandler}
            onClick={() => onSelectHolding && onSelectHolding(holding)}
            className={cn('rounded-sm', {
              'cursor-pointer': onSelectHolding,
            })}
            alt={holding.name}
            src={holding.imageURL}
            width={400}
            height={400}
          />
          {isStaked && (
            <div className="absolute top-2 left-2 right-2 bottom-0 rounded-sm bg-[url('/assets/frozenOverlay.png')] bg-no-repeat bg-center bg-cover pointer-events-none"></div>
          )}
        </div>
      </span>
      <span className="flex justify-between px-3 py-2 text-xs xs:text-sm">
        <span className="inline-flex gap-2 text-left">
          <p className="font-bold text-[white]">{splitName[0]}</p>
          <p className="text-[gray] tracking-widest">{splitName[1]}</p>
        </span>
      </span>
    </span>
  )
}

type StakeButtonProps = {
  holding: Holding
}

export const StakeButton = ({ holding }: StakeButtonProps) => {
  const { t } = useTranslation()
  const [unstakeDialogOpen, setUnstakeDialogOpen] = useState(false)
  const { publicKey } = useWallet()

  const blockedWallets = useMemo(() => {
    return ['9bC8QVbCRB36MbjnV16PTbJSLCmgky7cuzYPSUT2D6x6']
  }, [])

  const { isStaked, stake, stakeMetadataLoading, timeLeftForCooldwon } =
    useStaking({
      nft: holding.publicKey,
    })

  const handleClose = useCallback(() => {
    setUnstakeDialogOpen(false)
  }, [])

  const handleStake = useCallback(() => {
    if (!isStaked) {
      stake(holding.publicKey)
    } else {
      setUnstakeDialogOpen(true)
    }
  }, [isStaked, holding, stake])

  const isCoolingDown = useMemo(() => {
    const cooldownResponse = timeLeftForCooldwon()
    if (!cooldownResponse) return false
    return cooldownResponse.total > 0
  }, [timeLeftForCooldwon])

  const ButtonContent = useMemo(() => {
    if (stakeMetadataLoading) {
      return <LoadingCircle />
    }

    const cooldownResponse = timeLeftForCooldwon()

    if (isCoolingDown && cooldownResponse) {
      return t('holdings.holdingCard.cooldown', {
        days: cooldownResponse?.days,
        hours: cooldownResponse?.hours,
        minutes: cooldownResponse?.minutes,
        seconds: cooldownResponse?.seconds,
      })
    }

    if (!isStaked) return t('holdings.holdingCard.stake')
    return t('holdings.holdingCard.unstake')
  }, [stakeMetadataLoading, t, isStaked, isCoolingDown, timeLeftForCooldwon])

  return (
    <>
      {stake && (
        <span className="flex flex-col min-w-40 justify-center items-center p-2 gap-2">
          <Button
            ripple
            fullWidth
            onClick={handleStake}
            className={`justify-center flex ${isCoolingDown ? 'pointer-events-none' : ''}`}
            disabled={isCoolingDown}
          >
            {ButtonContent}
          </Button>
          {isCoolingDown && (
            <Button
              ripple
              fullWidth
              onClick={() => stake(holding.publicKey)}
              className={`justify-center flex`}
            >
              {t('holdings.holdingCard.stake')}
            </Button>
          )}
        </span>
      )}
      {!blockedWallets.includes(publicKey.toBase58()) && (
        <UnstakeDialog
          open={unstakeDialogOpen}
          onCloseHeader={handleClose}
          nft={holding.publicKey}
        />
      )}
    </>
  )
}

type CopyReferralButtonProps = {
  holding: Holding
}

export const CopyReferralButton = ({ holding }: CopyReferralButtonProps) => {
  const { t } = useTranslation()
  const [showToast, setShowToast] = useState(false)

  useEffect(() => {
    if (!showToast) return
    const timeout = setTimeout(() => {
      setShowToast(false)
    }, 3000)
    return () => clearTimeout(timeout)
  }, [showToast])

  const clickReferralHandler = () => {
    const referralCode = 'DeWiCat #' + holding.number
    navigator.clipboard.writeText(referralCode)
    setShowToast(true)
  }

  return (
    <>
      {showToast && <Toast title={t('holdings.holdingCard.copiedReferral')} />}
      <span className="w-fit h-fit gap-4 text-xs xs:text-sm lg:text-base flex justify-between items-center rounded-sm border border-solid border-[#383A3F] py-1 pl-3">
        <p className="text-[white] font-bold">
          {t('holdings.holdingCard.referral')}
        </p>
        <span className="flex items-center">
          <p className="text-[white] opacity-50 text-left xs:text-center">
            {t('holdings.holdingCard.name', { number: holding.number })}
          </p>
          <Button
            onClick={clickReferralHandler}
            className="bg-[transparent] pl-[0.3rem] pr-1.5 xs:pl-3.5 xs:pr-3.5 md:pr-3 md:pl-3 border-0"
          >
            <CopyToClipboardIcon />
          </Button>
        </span>
      </span>
    </>
  )
}

export default memo(HoldingCard)
